import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  errorMessage,
  useBulkMappings,
  useGlobalSettings,
  useGuilds,
  useMappings,
  type Mapping,
  type SettingValue,
  type SettingsSpec,
} from "@/api";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { SettingsForm, type SettingRowModel } from "@/components/SettingsForm";
import { useDocumentTitle } from "@/components/useDocumentTitle";
import { plural } from "@/lib/format";
import { mappingStatus } from "@/lib/status";
import {
  aggregate,
  formatValue,
  globalOverrides,
  humanize,
  resolve,
  touchesKey,
  type Resolved,
} from "@/lib/settings";
import {
  Badge,
  BulkBar,
  Button,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  Select,
  StatusDot,
  Table,
  useToast,
  type TableColumn,
} from "@/ui";
import "./pages.css";


export function MappingsPage() {
  useDocumentTitle("Mappings");
  const [params, setParams] = useSearchParams();
  const guildFilter = params.get("guild") ?? "";
  const enabledFilter = params.get("enabled") ?? "";
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const mappings = useMappings({
    source_guild_id: guildFilter || undefined,
    enabled: enabledFilter === "" ? undefined : enabledFilter === "true",
  });
  const guilds = useGuilds();
  const bulk = useBulkMappings();
  const toast = useToast();
  const navigate = useNavigate();

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
    setSelected(new Set());
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const items = mappings.data?.items ?? [];
    if (!q) return items;
    return items.filter(
      (m) =>
        m.source_channel.name.toLowerCase().includes(q) ||
        m.target_channel.name.toLowerCase().includes(q) ||
        m.source_channel.guild_name.toLowerCase().includes(q),
    );
  }, [mappings.data, search]);

  // Keep the selection to rows that still exist.
  const selectedRows = rows.filter((m) => selected.has(String(m.id)));
  const ids = selectedRows.map((m) => m.id);

  const runBulk = async (action: "enable" | "disable" | "delete") => {
    try {
      const res = await bulk.mutateAsync({ ids, action });
      const verb = { enable: "Enabled", disable: "Disabled", delete: "Deleted" }[action];
      toast.show({ tone: "success", title: `${verb} ${plural(res.updated, "mapping")}` });
      if (action === "delete") setSelected(new Set());
    } catch (e) {
      toast.show({ tone: "danger", title: "Bulk update failed", message: errorMessage(e) });
    }
  };

  const columns: TableColumn<Mapping>[] = [
    {
      key: "source",
      header: "Source",
      render: (m) => (
        <span className="mapping-cell">
          <Link className="link" to={`/mappings/${m.id}`} onClick={(e) => e.stopPropagation()}>
            #{m.source_channel.name}
          </Link>
          <span className="mapping-cell__sub">{m.source_channel.guild_name}</span>
        </span>
      ),
    },
    {
      key: "target",
      header: "Target",
      render: (m) => <span>#{m.target_channel.name}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (m) => {
        const s = mappingStatus(m);
        return <StatusDot status={s.status} label={s.label} />;
      },
    },
    {
      key: "overrides",
      header: "Overrides",
      render: (m) => {
        const keys = Object.keys(m.settings);
        return keys.length ? (
          <Badge tone="neutral" title={keys.map(humanize).join(", ")}>
            {plural(keys.length, "override")}
          </Badge>
        ) : (
          <span className="faint">inherits all</span>
        );
      },
    },
    { key: "id", header: "ID", align: "end", mono: true, render: (m) => String(m.id) },
  ];

  const guildOptions = [
    { value: "", label: "All servers" },
    ...(guilds.data?.items ?? []).map((g) => ({ value: g.id, label: g.name })),
  ];

  return (
    <div className="page">
      <PageHeader
        title="Mappings"
        description="Source channel → your channel. Select rows to edit settings for many mappings at once."
        actions={
          <Button variant="secondary" onClick={() => navigate("/sources")}>
            Map more channels
          </Button>
        }
      />
      <div className="filters" role="search">
        <Input
          className="filters__grow"
          type="search"
          label="Search"
          placeholder="Channel or server"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          label="Server"
          value={guildFilter}
          options={guildOptions}
          onChange={(e) => setParam("guild", e.target.value)}
        />
        <Select
          label="State"
          value={enabledFilter}
          options={[
            { value: "", label: "Enabled and disabled" },
            { value: "true", label: "Enabled" },
            { value: "false", label: "Disabled" },
          ]}
          onChange={(e) => setParam("enabled", e.target.value)}
        />
      </div>
      <ErrorBanner error={mappings.error} onRetry={() => mappings.refetch()} />

      <div>
        <Table
          caption="Mappings"
          columns={columns}
          rows={rows}
          getRowId={(m) => String(m.id)}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          loading={mappings.isLoading}
          onRowClick={(m) => navigate(`/mappings/${m.id}`)}
          empty={
            <EmptyState
              title={search || guildFilter || enabledFilter ? "No mappings match" : "No mappings yet"}
              description={
                search || guildFilter || enabledFilter
                  ? "Clear the filters to see every mapping."
                  : "Open Sources to map channels."
              }
              action={
                <Button variant="primary" onClick={() => navigate("/sources")}>
                  Open Sources
                </Button>
              }
            />
          }
        />
        <BulkBar
          count={selectedRows.length}
          noun={{ one: "mapping", other: "mappings" }}
          busy={bulk.isPending}
          onClear={() => setSelected(new Set())}
          actions={
            <>
              <Button size="sm" variant="primary" onClick={() => setEditorOpen(true)}>
                Edit settings…
              </Button>
              <Button size="sm" variant="secondary" disabled={bulk.isPending} onClick={() => runBulk("enable")}>
                Enable
              </Button>
              <Button size="sm" variant="secondary" disabled={bulk.isPending} onClick={() => runBulk("disable")}>
                Disable
              </Button>
              <Button size="sm" variant="danger" disabled={bulk.isPending} onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            </>
          }
        />
      </div>

      <BulkSettingsEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        mappings={selectedRows}
      />
      <ConfirmDialog
        open={confirmDelete}
        destructive
        title={`Delete ${plural(ids.length, "mapping")}`}
        message="Messages stop relaying for these channels. Target channels and their history stay in your server."
        confirmLabel={`Delete ${plural(ids.length, "mapping")}`}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          await runBulk("delete");
          setConfirmDelete(false);
        }}
      />
    </div>
  );
}

/**
 * Bulk settings editor: shows each mapping-level key aggregated across the selection.
 * A Switch is "mixed" when the selection disagrees; changing a control sets the value
 * on every selected mapping; reset deletes the mapping-level override for all of them.
 */
function BulkSettingsEditor({
  open,
  onClose,
  mappings,
}: {
  open: boolean;
  onClose: () => void;
  mappings: Mapping[];
}) {
  const global = useGlobalSettings();
  const guilds = useGuilds();
  const bulk = useBulkMappings();
  const toast = useToast();

  const spec: SettingsSpec | undefined = global.data?.spec;
  const resolved: Resolved[] = useMemo(() => {
    if (!spec) return [];
    const guildSettings = new Map((guilds.data?.items ?? []).map((g) => [g.id, g.settings]));
    return mappings.map((m) =>
      resolve(
        spec,
        globalOverrides(global.data),
        guildSettings.get(m.source_channel.guild_id),
        m.settings,
      ),
    );
  }, [spec, global.data, guilds.data, mappings]);

  const ids = mappings.map((m) => m.id);
  const n = plural(ids.length, "mapping");

  const row = (key: string): SettingRowModel => {
    const agg = aggregate(key, resolved);
    const overriddenCount = resolved.filter((r) => r.origin[key] === "mapping").length;
    const uniformOrigin = agg && agg.origins.length === 1 ? agg.origins[0] : undefined;
    return {
      value: agg?.kind === "uniform" ? agg.value : undefined,
      mixed: agg?.kind === "mixed",
      origin: uniformOrigin,
      partialOverride:
        uniformOrigin === undefined && overriddenCount > 0 ? overriddenCount : undefined,
      editable: true,
    };
  };

  const set = async (key: string, value: SettingValue) => {
    try {
      await bulk.mutateAsync({ ids, action: "set", set: { [key]: value } });
      toast.show({ tone: "success", title: `${humanize(key)}: ${formatValue(value)} for ${n}` });
    } catch (e) {
      toast.show({ tone: "danger", title: `Could not set ${humanize(key)}`, message: errorMessage(e) });
    }
  };
  const reset = async (key: string) => {
    try {
      await bulk.mutateAsync({ ids, action: "reset", reset: [key] });
      toast.show({ tone: "success", title: `${humanize(key)} reset to inherited for ${n}` });
    } catch (e) {
      toast.show({ tone: "danger", title: `Could not reset ${humanize(key)}`, message: errorMessage(e) });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`Edit settings · ${n}`}
      description="Changes apply immediately to every selected mapping. 'mixed' means the selection differs."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="bulk-editor">
        <ErrorBanner error={global.error} onRetry={() => global.refetch()} />
        {spec && mappings.length ? (
          <div className="bulk-editor__rows">
            <SettingsForm
              spec={spec}
              level="mapping"
              include={(k) => spec[k].levels.includes("mapping")}
              row={row}
              onSet={set}
              onReset={reset}
              saving={(k) =>
                bulk.isPending && touchesKey(k, bulk.variables?.set, bulk.variables?.reset)
              }
            />
          </div>
        ) : (
          <p className="muted">Select at least one mapping.</p>
        )}
      </div>
    </Modal>
  );
}
